import { T } from "../lib/fc3i18n";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Trophy } from "lucide-react";
import { SAFETY, totalSecs } from "./data";
const fmt = t => `${Math.floor(t / 60)}:${String(Math.max(0, t) % 60).padStart(2, "0")}`;
export function logCompleted(id) {
  try {
    const k = "fc_fit_done";
    const arr = JSON.parse(localStorage.getItem(k) || "[]");
    arr.push({
      id,
      at: Date.now()
    });
    localStorage.setItem(k, JSON.stringify(arr.slice(-200)));
  } catch {/* storage unavailable — fine */}
}
export function completedThisWeek() {
  try {
    const arr = JSON.parse(localStorage.getItem("fc_fit_done") || "[]");
    const since = Date.now() - 7 * 86400000;
    return arr.filter(x => x.at >= since).length;
  } catch {
    return 0;
  }
}
function beep(ctxRef, freq = 880, dur = 0.12) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!ctxRef.current) ctxRef.current = new AC();
    const ctx = ctxRef.current;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + dur);
  } catch {/* ignore */}
}
export default function WorkoutPlayer({
  workout,
  accent = "#E0306F",
  onClose
}) {
  const steps = workout.steps;
  const [idx, setIdx] = useState(-1); // -1 = intro
  const [left, setLeft] = useState(steps[0]?.secs || 0);
  const [running, setRunning] = useState(false);
  const [voice, setVoice] = useState(true);
  const [done, setDone] = useState(false);
  const audio = useRef(null);
  const dialog = useRef(null);
  const say = useCallback(text => {
    if (!voice || !("speechSynthesis" in window)) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1;
      u.pitch = 1.05;
      u.lang = "en-IN";
      window.speechSynthesis.speak(u);
    } catch {/* ignore */}
  }, [voice]);
  const goTo = useCallback(i => {
    if (i >= steps.length) {
      setRunning(false);
      setDone(true);
      logCompleted(workout.id);
      say("Workout complete. Great job!");
      return;
    }
    const st = steps[Math.max(0, i)];
    setIdx(Math.max(0, i));
    setLeft(st.secs);
    say(`${st.name}. ${st.cue}`);
  }, [steps, say, workout.id]);
  useEffect(() => {
    if (!running || done || idx < 0) return undefined;
    const t = setInterval(() => {
      setLeft(l => {
        if (l <= 4 && l > 1) beep(audio, 660);
        if (l <= 1) {
          beep(audio, 990, 0.25);
          setTimeout(() => goTo(idx + 1), 0);
          return 0;
        }
        return l - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, idx, done, goTo]);
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") onClose();
      if (e.key === " ") {
        e.preventDefault();
        setRunning(r => !r);
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      try {
        window.speechSynthesis?.cancel();
      } catch {/* ignore */}
    };
  }, [onClose]);
  const start = () => {
    goTo(0);
    setRunning(true);
  };
  const cur = idx >= 0 ? steps[idx] : null;
  const next = idx + 1 < steps.length ? steps[idx + 1] : null;
  const elapsedBefore = steps.slice(0, Math.max(0, idx)).reduce((t, x) => t + x.secs, 0);
  const total = totalSecs(workout);
  const progress = idx < 0 ? 0 : Math.min(1, (elapsedBefore + (cur ? cur.secs - left : 0)) / total);
  const R = 92;
  const C = 2 * Math.PI * R;
  const frac = cur ? left / cur.secs : 1;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={`${workout.title} workout player`}>
      <div className="absolute inset-0 bg-[#1E1A2B]/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div ref={dialog} tabIndex={-1} className="relative w-full max-w-[560px] max-h-[96vh] overflow-y-auto rounded-[32px] bg-white shadow-2xl outline-none">
        <div className="h-1.5 bg-[#F3EEF6] rounded-t-[32px] overflow-hidden">
          <div className="h-full transition-all duration-1000 ease-linear" style={{
          width: `${progress * 100}%`,
          background: accent
        }} />
        </div>
        <div className="flex items-center justify-between px-6 pt-5">
          <div>
            <p className="text-[12px] font-extrabold uppercase tracking-wider" style={{
            color: accent
          }}>{T(workout.level)} · {workout.minutes} {T("min")}</p>
            <h2 className="text-[22px] font-extrabold text-[#1E1A2B] leading-tight">{T(workout.title)}</h2>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setVoice(v => !v)} className="w-11 h-11 rounded-full hover:bg-[#F3EEF6] flex items-center justify-center text-[#2B2140]" aria-label={voice ? T("Turn voice guidance off") : T("Turn voice guidance on")}>
              {voice ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button type="button" onClick={onClose} className="w-11 h-11 rounded-full hover:bg-[#F3EEF6] flex items-center justify-center text-[#2B2140]" aria-label={T("Close workout")}>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {done ? <div className="px-6 py-10 text-center">
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center" style={{
          background: "#FFF1DC"
        }}>
              <Trophy className="w-10 h-10 text-[#E8932C]" />
            </div>
            <h3 className="mt-5 text-[28px] font-extrabold text-[#1E1A2B]">{T("Workout complete!")}</h3>
            <p className="mt-2 text-[16px] text-[#5F5A70]">{T("You moved for {{m}} minutes. You've finished {{n}} workout(s) this week on this device.", { m: Math.round(total / 60), n: completedThisWeek() })}</p>
            <button type="button" onClick={onClose} className="mt-7 fc-btn-coral inline-flex items-center justify-center min-h-[52px] rounded-full px-8 font-extrabold">{T("Done")}</button>
          </div> : idx < 0 ? <div className="px-6 py-6">
            <p className="text-[16px] text-[#5F5A70]">{T(workout.blurb)}</p>
            <ol className="mt-5 space-y-2 max-h-[38vh] overflow-y-auto pr-1">
              {steps.map((st, i) => <li key={i} className={`flex items-center justify-between rounded-2xl px-4 py-3 text-[15px] ${st.rest ? "bg-[#F6F7F9] text-[#7A7488]" : "bg-[#FFF8F9] text-[#1E1A2B]"}`}>
                  <span className="font-semibold">{i + 1}. {T(st.name)}</span>
                  <span className="tabular-nums text-[#5F5A70]">{fmt(st.secs)}</span>
                </li>)}
            </ol>
            <p className="mt-4 text-[12.5px] leading-relaxed text-[#7A7488]">{T(SAFETY)}</p>
            <button type="button" onClick={start} className="mt-5 w-full fc-btn-coral inline-flex items-center justify-center gap-2 min-h-[56px] rounded-full font-extrabold text-[17px]" data-testid="workout-start">
              <Play className="w-5 h-5" fill="currentColor" /> {T("Start workout")}</button>
          </div> : <div className="px-6 pt-4 pb-7 text-center">
            <p className="text-[13px] font-bold text-[#7A7488]">{T("Step {{i}} of {{n}}", { i: idx + 1, n: steps.length })}</p>
            <div className="relative mx-auto mt-3 w-[220px] h-[220px]">
              <svg viewBox="0 0 220 220" className="w-full h-full -rotate-90" aria-hidden="true">
                <circle cx="110" cy="110" r={R} fill="none" stroke="#F3EEF6" strokeWidth="14" />
                <circle cx="110" cy="110" r={R} fill="none" stroke={cur.rest ? "#9BE3CF" : accent} strokeWidth="14" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - frac)} style={{
              transition: "stroke-dashoffset 1s linear"
            }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[56px] font-extrabold tabular-nums text-[#1E1A2B] leading-none" aria-live="off">{fmt(left)}</span>
                {cur.rest && <span className="mt-1 text-[13px] font-bold text-[#13896F]">{T("Rest")}</span>}
              </div>
            </div>
            <h3 className="mt-4 text-[26px] font-extrabold text-[#1E1A2B] leading-tight" aria-live="polite">{T(cur.name)}</h3>
            <p className="mt-2 text-[16px] text-[#5F5A70] max-w-md mx-auto">{T(cur.cue)}</p>
            {next && <p className="mt-4 text-[14px] text-[#7A7488]">{T("Next:")} <span className="font-bold text-[#2B2140]">{T(next.name)}</span></p>}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button type="button" onClick={() => goTo(Math.max(0, idx - 1))} className="w-14 h-14 rounded-full bg-[#F3EEF6] text-[#2B2140] flex items-center justify-center" aria-label={T("Previous step")}><SkipBack className="w-5 h-5" /></button>
              <button type="button" onClick={() => setRunning(r => !r)} className="w-20 h-20 rounded-full text-white flex items-center justify-center shadow-lg" style={{
            background: accent
          }} aria-label={running ? T("Pause") : T("Resume")}>
                {running ? <Pause className="w-8 h-8" fill="currentColor" /> : <Play className="w-8 h-8" fill="currentColor" />}
              </button>
              <button type="button" onClick={() => goTo(idx + 1)} className="w-14 h-14 rounded-full bg-[#F3EEF6] text-[#2B2140] flex items-center justify-center" aria-label={T("Next step")}><SkipForward className="w-5 h-5" /></button>
            </div>
            <p className="mt-5 text-[12px] text-[#9A94A8]">{T("Space to pause · Esc to close · Stop if anything hurts.")}</p>
          </div>}
      </div>
    </div>;
}